import { ConsoleError, type ConsoleApi, type ErrorCode } from './types'

// The console on console.purlview.dev calls the account host, which is the
// same site (D-051): the session cookie travels with `credentials: include`,
// the account host answers CORS for this origin only, and every state change
// carries the session's or the challenge's token in Purlview-CSRF.
export function httpApi(origin: string): ConsoleApi {
  const base = origin.replace(/\/$/, '') + '/console/v1/'

  async function call<T>(path: string, init: { csrf?: string; body?: unknown } = {}): Promise<T> {
    const headers: Record<string, string> = {}
    if (init.body !== undefined) headers['Content-Type'] = 'application/json'
    if (init.csrf) headers['Purlview-CSRF'] = init.csrf
    let res: Response
    try {
      res = await fetch(base + path, {
        method: init.body === undefined ? 'GET' : 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers,
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
      })
    } catch {
      throw new ConsoleError('network', 0)
    }
    if (res.status === 204) return undefined as T
    let data: any = null
    try {
      data = await res.json()
    } catch {
      data = null
    }
    if (!res.ok) {
      const code = (typeof data?.error === 'string' ? data.error : res.status >= 500 ? 'unavailable' : 'forbidden') as ErrorCode
      throw new ConsoleError(code, res.status, data?.attempts_left ?? 0, data?.email ?? '')
    }
    return data as T
  }

  return {
    session: () => call('session'),
    startSignIn: (csrf, email) => call('signin', { csrf, body: { email } }),
    verifySignIn: (csrf, code) => call('signin/code', { csrf, body: { code } }),
    resendSignIn: (csrf) => call('signin/again', { csrf, body: {} }),
    signOut: (csrf) => call('signout', { csrf, body: {} }),
    shares: () => call('shares'),
    stopShare: (csrf, share) => call('shares/stop', { csrf, body: { share } }),
    devices: () => call('devices'),
    signOutDevice: (csrf, id) => call('devices/signout', { csrf, body: { id } }),
    browsers: () => call('browsers'),
    signOutBrowser: (csrf, id) => call('browsers/signout', { csrf, body: { id } }),
    regions: () => call('regions'),
    voteRegion: (csrf, region) => call('regions/vote', { csrf, body: { region } }),
  }
}
